// kfe-l2b-drag-area.js — drag-and-drop tile grid for L2B Training and Testing.
//
// Adapted from KFE Web's <kfe-l2b-drag-area> (Brief 003). Logic is unchanged —
// the only edits are: import paths (`../core/*`, `../data/media-paths.js`),
// design tokens renamed `--kfe-*` → `--l2b-*` (the demo's token set; the dojo
// skin is Brief 006), and the UI strings keyed through `t()` (Brief 004). The
// host re-creates this element on every render, so a language change re-renders
// it with the active L1's strings without a local subscription.
//
// PORT TARGET (unchanged from KFE):
//   • iEnglish KFEContentsView.m — pan-driven tile grid, single-drag policy
//   • iEnglish KFETargetsView.m  — drop-target hit test
//
// L2B is drag-the-correct-tile-into-the-target-box, not click-to-pick. Renders a
// 4-per-row image grid + a drop zone underneath and emits "pick" with detail =
// { mmuId } when a tile is released over the drop zone — same event shape the
// host screen's _handlePick path expects. Used by BOTH Training and Testing.
//
// Why Pointer Events instead of HTML5 drag: dragstart/drop don't fire on touch
// on iOS Safari without polyfill; pointerdown/move/up + setPointerCapture works
// uniformly across mouse, touch, and pen.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t } from "../core/i18n.js";
import { resolveImage } from "../data/media-paths.js";

// Tap-vs-drag discrimination (Brief 051). A press that stays within TAP_SLOP_PX
// and is released within TAP_MAX_MS is a tap (→ enlarge preview); any movement
// past the slop commits to a drag.
const TAP_SLOP_PX = 10;
const TAP_MAX_MS = 300;

export class KFEL2BDragArea extends KFEComponent {
  // `urls` (Brief 134): an optional host-supplied { mmuId → imageUrl } map. When
  // the L2B Training screen pre-resolves + preloads all tile images before
  // swapping the visible frame (atomic tile renewal), it passes the map in so
  // this component renders images on its FIRST paint instead of resolving them
  // asynchronously in onConnected. Callers that don't pre-resolve (Testing) omit
  // it and fall back to the self-resolution loop below.
  static props = ["items", "disabled", "urls"];
  static styles = `
    :host { display: block; }
    /* 4 cols at every width so the 8-tile cap (L2BConfig.maxGridSize) fits in 2
       rows, keeping the drop zone on-screen (brief 006c). Smaller grids (≤4
       tiles) still collapse to a single row. Square tiles (aspect-ratio 1/1)
       stay sacrosanct; tap-to-enlarge covers legibility.

       Brief 013a — THE TILE CAP. The columns were 1fr, so tile size tracked the
       playfield width: in the ~740px boxed embed they ballooned to ~175px and the
       two rows (always two — maxGridSize 8, 4/row) ate ~350px that no boxed height
       could absorb, pushing the drop zone below the fold. minmax(0, ...) still
       behaves exactly like 1fr while space is tight — a track only grows up to
       its max — so narrow phones are unchanged; the cap bites only once the box is
       wide enough to have made tiles too big. The dvh term keeps a SHORT box
       honest too (a wide-and-short embed has less vertical room to spend on tiles
       than a wide-and-tall one) and dvh is URL-bar-safe (Gotcha 3).
       justify-content centres the capped grid in a wider playfield. */
    .grid { display: grid; gap: 0.5rem; justify-content: center;
            grid-template-columns: repeat(4, minmax(0, min(128px, 21dvh))); }
    /* Warm-paper tile background — L2B images are transparent-PNG line drawings
       designed for white-paper rendering. Routed through --l2b-image-bg. The
       gold border + lift make them read as dojo cards; square tiles (aspect-ratio
       1/1) are sacrosanct (brief Gotcha 4) and unchanged. */
    .tile { aspect-ratio: 1 / 1; background: var(--l2b-image-bg);
            border: 2px solid var(--l2b-border); border-radius: 10px;
            box-shadow: 0 2px 6px rgba(0,0,0,0.4);
            display: flex; align-items: center; justify-content: center;
            overflow: hidden; padding: 0; position: relative;
            cursor: grab;
            /* touch-action:none stops iOS scroll-on-drag from stealing the
               pointer stream the moment the finger moves. */
            touch-action: none;
            -webkit-user-select: none; user-select: none;
            -webkit-tap-highlight-color: transparent; }
    /* Brief 011b: the image-resolution window. The tile shows a neutral shimmer
       placeholder (never the phrase), so a round advance reads as "loading," not
       as the answer word. The warm-paper .tile background shows through. */
    .tile.no-image .tile-ph { position: absolute; inset: 0;
                     background: linear-gradient(100deg,
                       rgba(255,255,255,0) 35%,
                       rgba(255,255,255,0.28) 50%,
                       rgba(255,255,255,0) 65%);
                     background-size: 200% 100%;
                     animation: tile-shimmer 1.2s ease-in-out infinite; }
    @keyframes tile-shimmer {
      from { background-position: 150% 0; }
      to   { background-position: -150% 0; }
    }
    @media (prefers-reduced-motion: reduce) {
      .tile.no-image .tile-ph { animation: none; }
    }
    .tile img { width: 100%; height: 100%; object-fit: contain;
                pointer-events: none; -webkit-user-drag: none; }
    .tile.dragging { cursor: grabbing; z-index: 10;
                     box-shadow: 0 8px 16px rgba(0, 0, 0, 0.4); }
    .tile.snapback { transition: transform 0.3s ease-out; }
    .tile.frozen { opacity: 0.45; }

    /* Drop zone: square, sized smaller than the grid, pushed below it so it reads
       as its own central drop target (iOS L2B canon). Skinned with the dojo Carpet
       sprite as a woven mat; dashed gold edge, lacquer-red on hover. */
    .target-zone {
      margin: 3rem auto 0;
      width: clamp(180px, 45%, 280px);
      aspect-ratio: 1 / 1;
      padding: 1rem; box-sizing: border-box;
      border: 3px dashed var(--l2b-border);
      border-radius: 12px;
      background:
        linear-gradient(rgba(20,12,8,0.18), rgba(20,12,8,0.18)),
        var(--l2b-skin-carpet, none) center / cover no-repeat,
        color-mix(in srgb, var(--l2b-fg) 25%, var(--l2b-bg));
      box-shadow: inset 0 0 24px rgba(0,0,0,0.45), 0 4px 12px rgba(0,0,0,0.4);
      display: flex; align-items: center; justify-content: center;
      color: var(--l2b-fg); text-align: center; font-size: 0.95rem;
      font-weight: 600; text-shadow: 0 1px 3px rgba(0,0,0,0.7);
      transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
    }
    .target-zone.hover {
      border-style: solid; border-color: var(--l2b-accent);
      box-shadow: inset 0 0 24px rgba(0,0,0,0.3),
                  0 0 0 2px var(--l2b-accent), 0 0 18px rgba(224,85,43,0.6);
      transform: scale(1.03);
    }
    /* Brief 013 — mobile-fit. The drop zone is the biggest single vertical sink
       and sits LAST in the column, so on any viewport shorter than a full phone
       its 3rem top margin + ~180px square push "Drop here" off-screen. On short
       viewports (the phone full-screen tab AND the short desktop 16:10 inline
       frame, which both trip the height query — a tall desktop window does not)
       cut the margin and size the square relative to viewport height so it
       shrinks on short screens instead of being pushed off. dvh is URL-bar-safe
       (Gotcha 3) and clamps to a ~130px floor so it stays a usable drag target
       (success criterion 2). aspect-ratio 1/1 stays sacrosanct (Gotcha 1). */
    @media (max-height: 720px), (max-width: 600px) {
      .target-zone {
        margin: 0.75rem auto 0;
        width: clamp(130px, min(45%, 26dvh), 280px);
      }
    }

    /* Brief 013a — THE SIDE-BY-SIDE REFLOW. Capping the tiles is necessary but
       not sufficient. In a boxed embed the vertical budget is about
       box - 78px (shell header + footer) - 32px (main padding) - ~160px
       (progress row + h2 + target row + the reserved feedback slot), which leaves
       ~250px at a 520px box for BOTH tile rows AND the drop zone when they are
       stacked. Two rows of tiles alone are ~230px at any size still worth
       tapping, so a stacked drop zone does not fit — measured on the harness, not
       estimated. Height is the scarce axis in a boxed embed and width is the
       abundant one, so once the playfield is wide enough the drop zone moves
       BESIDE the grid instead of under it, and the whole playfield fits without
       shrinking the tiles into uselessness. This is the same move the 020 reveal
       makes for the same reason at the same 480px floor: below it two columns
       stop being readable and the stacked layout — which a tall phone has the
       room for — stays exactly as it was.

       The 019/019a drag path is untouched: _overTarget() hit-tests the zone's
       LIVE getBoundingClientRect(), so it follows the zone wherever it lands, and
       the drag becomes a horizontal gesture — the direction 019a records as the
       one iOS never stole. The tap-to-enlarge overlay is position:fixed, hence
       out of flow and not a flex item. */
    @media (min-width: 480px) {
      :host { display: flex; align-items: center; justify-content: center;
              gap: 1.25rem; }
      .grid { flex: 1 1 auto; min-width: 0; }
      .target-zone { flex: none; margin: 0;
                     width: clamp(120px, min(30%, 30dvh), 240px); }
    }

    /* Brief 013a — a phone in landscape (~390px tall) is the shortest box that
       ships, and even beside the grid the playfield needs one more notch there.
       Below 420px the tile cap and the zone both tighten; above it the 440px
       embed keeps its larger tiles. */
    @media (max-height: 420px) {
      .grid { grid-template-columns: repeat(4, minmax(0, min(128px, 17dvh))); }
      .target-zone { width: clamp(104px, min(30%, 28dvh), 240px); }
    }

    /* Tap-to-enlarge preview (Brief 051). A single tap floats the tile image up
       to a full-bleed WHITE backdrop sized to a shorter-viewport-side square;
       tapping anywhere dismisses it. The enter animation is driven via
       @starting-style so the RESTING state is the visible one. */
    .preview-overlay {
      position: fixed; inset: 0; z-index: 1000;
      display: flex; align-items: center; justify-content: center;
      background: var(--l2b-image-bg);
      opacity: 1; transition: opacity 0.3s ease;
      cursor: zoom-out; touch-action: manipulation;
      -webkit-tap-highlight-color: transparent; outline: none;
    }
    .preview-overlay .preview-image {
      width: min(100vw, 100vh); height: min(100vw, 100vh);
      max-width: 100%; max-height: 100%;
      object-fit: contain; background: var(--l2b-image-bg);
      transform: scale(1); transition: transform 0.3s ease;
      -webkit-user-select: none; user-select: none;
      -webkit-user-drag: none; pointer-events: none;
    }
    /* Brief 012: the enlarge overlay's unresolved-image fallback. A neutral
       shimmer box sized like the image (never the phrase) — the rare tap-to-
       enlarge before an image resolves reads as "loading," not as the answer
       word. Reuses the tile-shimmer keyframes; aria-label keeps the phrase. */
    .preview-overlay .preview-ph {
      width: min(100vw, 100vh); height: min(100vw, 100vh);
      max-width: 100%; max-height: 100%;
      background: linear-gradient(100deg,
                    rgba(255,255,255,0) 35%,
                    rgba(255,255,255,0.28) 50%,
                    rgba(255,255,255,0) 65%),
                  var(--l2b-image-bg);
      background-size: 200% 100%, 100% 100%;
      animation: tile-shimmer 1.2s ease-in-out infinite;
    }
    @media (prefers-reduced-motion: reduce) {
      .preview-overlay .preview-ph { animation: none; }
    }
    @starting-style {
      .preview-overlay { opacity: 0; }
      .preview-overlay .preview-image { transform: scale(0.92); }
    }
  `;

  constructor() {
    super();
    /** @type {Record<number,string>|undefined} */ this._urls = undefined;
    /** @type {{ tile: HTMLElement, pointerId: number, startX: number, startY: number, startTime: number, mmuId: number, mmu: any, committed: boolean } | null} */
    this._activeDrag = null;
    /** Last pointer position seen during a drag. Drives the drop decision when
     *  the release arrives via lostpointercapture/pointercancel — those events'
     *  own coordinates can be stale — instead of a clean pointerup (Brief 006d). */
    this._lastX = 0;
    this._lastY = 0;
    /** Tile MMU currently shown enlarged in the preview overlay, or null. */
    /** @type {any|null} */ this._previewMmu = null;
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);
    this._onPointerCancel = this._onPointerCancel.bind(this);
    this._onLostPointerCapture = this._onLostPointerCapture.bind(this);
    this._onTouchMove = this._onTouchMove.bind(this);
  }

  async onConnected() {
    // Brief 019a: bind the non-passive `touchmove` suppressor ONCE per mount,
    // before any early return — NOT per-drag. On iOS Safari decides at
    // `touchstart` whether a gesture may be prevented, from the non-passive
    // `touchmove` listeners present *at that instant*; `pointerdown` fires after
    // that decision. Binding it in _onPointerDown (Brief 019) was therefore too
    // late for the page's scroll axis: a straight vertical-down drag in
    // fullscreen was already committed to a pan and ignored our preventDefault
    // (horizontal, not the primary scroll axis, still got caught — hence the
    // directional signature). The handler self-gates on `_activeDrag`, so while
    // no drag is live this is a no-op and normal page scroll is unaffected.
    window.addEventListener("touchmove", this._onTouchMove, { passive: false });
    this.addCleanup(() => window.removeEventListener("touchmove", this._onTouchMove));

    const self = /** @type {any} */ (this);
    if (!self.items) return;
    // Brief 134: if the host pre-resolved the tile URLs and passed them via the
    // `urls` prop, render() already reads them on first paint — skip the async
    // self-resolution loop entirely.
    if (self.urls && Object.keys(self.urls).length) return;
    this._urls = /** @type {Record<number,string>} */ ({});
    for (const m of self.items) {
      const url = await resolveImage(m);
      if (url) this._urls[m.mmuId] = url;
    }
    this._scheduleRender();
  }

  /** Resolved tile URL map — host-supplied (`urls` prop) wins over the
   *  component's own self-resolved cache. Keyed by mmuId. (Brief 134)
   *  @returns {Record<number,string>|undefined} */
  _resolvedUrls() {
    const injected = /** @type {any} */ (this).urls;
    if (injected && Object.keys(injected).length) return injected;
    return this._urls;
  }

  onDisconnected() {
    // Mid-drag teardown (component removed by host re-render). Skip the
    // snap-back animation — there's nothing to animate to.
    this._endDrag(/* dropped */ false, /* skipSnapback */ true);
  }

  /**
   * @param {PointerEvent} e
   * @param {HTMLElement} tile
   * @param {any} mmu
   */
  _onPointerDown(e, tile, mmu) {
    if (this._activeDrag) return;
    if (this._previewMmu) return; // ignore tile presses while a preview is up
    if (/** @type {any} */ (this).disabled) return;
    // Primary button only on mouse; touch and pen always report button = 0.
    if (!e.isPrimary || e.button !== 0) return;

    e.preventDefault();
    // Brief 006d: capture the pointer on the HOST (a stable element), NOT the
    // tile. The tile is per-item, gets transformed during the drag, and can be
    // replaced by atomic tile renewal — capturing on a volatile element let the
    // browser revoke capture mid-gesture and deliver the release as
    // `lostpointercapture`/`pointercancel` instead of a clean `pointerup`, so the
    // drop never committed even though the hover highlight (driven by pointermove)
    // had already gone red. The host survives the whole gesture.
    try { this.setPointerCapture(e.pointerId); } catch { /* not supported */ }

    // Don't commit to a drag yet — a press might still resolve to a tap (→
    // preview). The freeze/dragging visuals are deferred to _commitDrag().
    this._activeDrag = {
      tile,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startTime: performance.now(),
      mmuId: mmu.mmuId,
      mmu,
      committed: false,
    };
    this._lastX = e.clientX;
    this._lastY = e.clientY;

    // Brief 135a: bind move/up/cancel at the WINDOW, not the tile, so a release
    // outside the viewport still ends the drag. lostpointercapture is bound to
    // the HOST (the capture target) and acts as a release backstop (Brief 006d).
    window.addEventListener("pointermove", this._onPointerMove);
    window.addEventListener("pointerup", this._onPointerUp);
    window.addEventListener("pointercancel", this._onPointerCancel);
    this.addEventListener("lostpointercapture", this._onLostPointerCapture);
    // The non-passive `touchmove` suppressor is NOT bound here — it lives for
    // the element's lifetime (bound in onConnected, Brief 019a) so it is already
    // registered when Safari makes its at-touchstart passive decision.
  }

  /** Every drag-end path funnels through here — no orphaned/stacked listeners. */
  _teardownDragListeners() {
    window.removeEventListener("pointermove", this._onPointerMove);
    window.removeEventListener("pointerup", this._onPointerUp);
    window.removeEventListener("pointercancel", this._onPointerCancel);
    this.removeEventListener("lostpointercapture", this._onLostPointerCapture);
    // `touchmove` is deliberately absent — it is a per-mount listener now
    // (Brief 019a), torn down by the onConnected addCleanup on unmount.
  }

  /**
   * Suppress the native touch-scroll/pan for the duration of a drag (Brief 019).
   * Bound `{ passive: false }` so preventDefault actually takes effect on iOS,
   * and bound for the element's whole lifetime (Brief 019a) — the `_activeDrag`
   * gate below, not the listener's lifetime, is what keeps page scroll alive.
   * @param {TouchEvent} e
   */
  _onTouchMove(e) {
    if (!this._activeDrag) return;
    if (e.cancelable) e.preventDefault();
  }

  /** Promote the current press to an active drag (mirrors iOS pan kicking in). */
  _commitDrag() {
    if (!this._activeDrag || this._activeDrag.committed) return;
    const { tile } = this._activeDrag;
    // Single-drag policy: freeze the rest. Mirrors iOS setDragViewIsCanDrag.
    this.shadowRoot?.querySelectorAll(".tile").forEach((node) => {
      if (node !== tile) node.classList.add("frozen");
    });
    tile.classList.remove("snapback");
    tile.classList.add("dragging");
    this._activeDrag.committed = true;
  }

  /**
   * Hit-test a viewport point against the drop zone's CURRENT rect. Recomputed
   * live (Brief 006d) rather than cached at pointerdown: <main> became scrollable
   * (Brief 006b), so a mid-drag scroll would desync a cached rect. Hover and the
   * drop decision both call this, so they stay in lockstep (Gotcha 1 — never
   * loosen one side of the test).
   * @param {number} x @param {number} y @returns {boolean}
   */
  _overTarget(x, y) {
    const el = this.shadowRoot?.querySelector(".target-zone");
    if (!el) return false;
    const r = el.getBoundingClientRect();
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  }

  /** @param {PointerEvent} e */
  _onPointerMove(e) {
    if (!this._activeDrag || e.pointerId !== this._activeDrag.pointerId) return;
    this._lastX = e.clientX;
    this._lastY = e.clientY;
    const { tile, startX, startY } = this._activeDrag;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    if (!this._activeDrag.committed) {
      if (Math.hypot(dx, dy) < TAP_SLOP_PX) return; // still inside tap slop
      this._commitDrag();
    }
    tile.style.transform = `translate(${dx}px, ${dy}px)`;

    const targetEl = this.shadowRoot?.querySelector(".target-zone");
    targetEl?.classList.toggle("hover", this._overTarget(e.clientX, e.clientY));
  }

  /** @param {PointerEvent} e */
  _onPointerUp(e) {
    if (!this._activeDrag || e.pointerId !== this._activeDrag.pointerId) return;
    const drag = this._activeDrag;
    this._lastX = e.clientX;
    this._lastY = e.clientY;
    const moved = Math.hypot(e.clientX - drag.startX, e.clientY - drag.startY) >= TAP_SLOP_PX;

    if (!drag.committed && !moved) {
      // Short, stationary press → a tap. Toggle the enlarge preview.
      const quick = performance.now() - drag.startTime < TAP_MAX_MS;
      const mmu = drag.mmu;
      this._endDrag(/* dropped */ false, /* skipSnapback */ true);
      if (quick) this._togglePreview(mmu);
      return;
    }

    // Drag release: drop counts only if released inside the (live) target rect.
    const dropped = drag.committed && this._overTarget(e.clientX, e.clientY);
    const mmuId = drag.mmuId;
    this._endDrag(dropped, /* skipSnapback */ !drag.committed);
    if (dropped) this.emit("pick", { mmuId });
  }

  /** @param {PointerEvent} e */
  _onPointerCancel(e) {
    this._releaseViaCapture(e);
  }

  /**
   * Browser revoked pointer capture without a clean pointerup reaching us — the
   * gesture ended via lostpointercapture or pointercancel. Route both through the
   * same backstop (Brief 006d).
   * @param {PointerEvent} e
   */
  _onLostPointerCapture(e) {
    this._releaseViaCapture(e);
  }

  /**
   * Backstop release path (Brief 006d). When the release is delivered as
   * pointercancel/lostpointercapture instead of pointerup, decide the drop from
   * the LAST pointer position (those events' own coords can be stale): inside the
   * drop zone → commit the pick; otherwise snap home (preserving Brief 135a's
   * snap-home-on-genuine-capture-loss). This is what makes "highlight went red,
   * finger lifted, nothing happened" commit on browsers that end the gesture with
   * capture loss. A normal pointerup commits first and nulls _activeDrag, so this
   * then no-ops — no double-commit.
   * @param {PointerEvent} e
   */
  _releaseViaCapture(e) {
    if (!this._activeDrag || e.pointerId !== this._activeDrag.pointerId) return;
    const drag = this._activeDrag;
    const dropped = drag.committed && this._overTarget(this._lastX, this._lastY);
    const mmuId = drag.mmuId;
    this._endDrag(dropped, /* skipSnapback */ !drag.committed);
    if (dropped) this.emit("pick", { mmuId });
  }

  /**
   * @param {boolean} dropped
   * @param {boolean} [skipSnapback]
   */
  _endDrag(dropped, skipSnapback = false) {
    if (!this._activeDrag) return;
    const { tile, pointerId } = this._activeDrag;

    try { this.releasePointerCapture(pointerId); } catch { /* not captured */ }
    this._teardownDragListeners();
    tile.classList.remove("dragging");

    if (dropped || skipSnapback) {
      // On a successful drop the host re-renders with new tiles — there's no
      // visible tile to animate. On disconnect there's nothing left to do.
      tile.style.transform = "";
    } else {
      // Snap back via CSS transition.
      tile.classList.add("snapback");
      tile.style.transform = "";
      const cleanup = () => {
        tile.classList.remove("snapback");
        tile.removeEventListener("transitionend", cleanup);
      };
      tile.addEventListener("transitionend", cleanup);
    }

    const targetEl = this.shadowRoot?.querySelector(".target-zone");
    targetEl?.classList.remove("hover");
    this.shadowRoot?.querySelectorAll(".tile").forEach((node) => {
      node.classList.remove("frozen");
    });
    this._activeDrag = null;
  }

  // Tap-to-enlarge preview (Brief 051). The drag-area owns this purely as a
  // view affordance — it never touches engine state.
  /** @param {any} mmu */
  _togglePreview(mmu) {
    if (this._previewMmu && this._previewMmu.mmuId === mmu.mmuId) {
      this._dismissPreview();
      return;
    }
    this._previewMmu = mmu;
    this.emit("kfe:tile-preview", { mmuId: mmu.mmuId });
    this._scheduleRender();
    // Pull focus to the overlay once it renders, for keyboard / VoiceOver
    // dismissal. setTimeout (not rAF) so it fires even when the tab is throttled.
    setTimeout(() => {
      const ov = /** @type {HTMLElement|null} */ (
        this.shadowRoot?.querySelector(".preview-overlay") ?? null
      );
      ov?.focus();
    }, 0);
  }

  _dismissPreview() {
    if (!this._previewMmu) return;
    const mmuId = this._previewMmu.mmuId;
    this._previewMmu = null;
    this.emit("kfe:tile-preview-dismiss", { mmuId });
    this._scheduleRender();
  }

  /** @param {any} mmu @returns {HTMLElement} */
  _buildPreviewOverlay(mmu) {
    const overlay = document.createElement("div");
    overlay.className = "preview-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-label", `Enlarged image: ${mmu.phrase}`);
    overlay.tabIndex = 0;

    const url = this._resolvedUrls()?.[mmu.mmuId];
    if (url) {
      const img = document.createElement("img");
      img.src = url;
      img.alt = mmu.phrase;
      img.className = "preview-image l2b-image-container";
      img.draggable = false;
      overlay.appendChild(img);
    } else {
      // Brief 012: NEVER render mmu.phrase as text. If the image isn't resolved
      // yet (rare: tap-to-enlarge inside the resolution window), show a neutral
      // shimmer box, not the word — same audio-only rule as the tile grid (011b).
      // A11y is unaffected: the overlay's aria-label keeps the phrase.
      const ph = document.createElement("div");
      ph.className = "preview-ph";
      ph.setAttribute("aria-hidden", "true");
      overlay.appendChild(ph);
    }

    // Tap anywhere on the overlay (iOS: backgroundView tap → hideBigImageView).
    overlay.addEventListener("click", () => this._dismissPreview());
    // Keyboard / VoiceOver dismissal — Escape, or activating the focused dialog.
    overlay.addEventListener("keydown", (e) => {
      const k = /** @type {KeyboardEvent} */ (e).key;
      if (k === "Escape" || k === "Enter" || k === " ") {
        e.preventDefault();
        this._dismissPreview();
      }
    });

    return overlay;
  }

  render() {
    const items = /** @type {any[]} */ (/** @type {any} */ (this).items) ?? [];

    const urls = this._resolvedUrls();
    const grid = document.createElement("div");
    grid.className = "grid";
    for (const m of items) {
      const tile = document.createElement("div");
      tile.className = "tile l2b-image-container" + (urls?.[m.mmuId] ? "" : " no-image");
      tile.setAttribute("role", "button");
      tile.setAttribute("aria-label", m.phrase);
      if (urls?.[m.mmuId]) {
        const img = document.createElement("img");
        img.src = urls[m.mmuId];
        img.alt = m.phrase;
        img.draggable = false;
        img.loading = "lazy";
        tile.appendChild(img);
      } else {
        // Brief 011b: NEVER render m.phrase as text. During the brief
        // image-resolution window (round advance, self-resolve first paint) the
        // tile must show a neutral shimmer placeholder, not the word — showing it
        // breaks the 006e audio-only rule and, in the Mandarin-default variant,
        // leaks the English translation (the answer). A11y is unaffected: the
        // phrase stays on the tile's aria-label (and the <img alt> when resolved).
        const ph = document.createElement("span");
        ph.className = "tile-ph";
        ph.setAttribute("aria-hidden", "true");
        tile.appendChild(ph);
      }
      tile.addEventListener("pointerdown", (e) => {
        this._onPointerDown(/** @type {PointerEvent} */ (e), tile, m);
      });
      grid.appendChild(tile);
    }

    const targetZone = document.createElement("div");
    targetZone.className = "target-zone";
    targetZone.textContent = t("l2b.dropHere");

    const overlay = this._previewMmu
      ? this._buildPreviewOverlay(this._previewMmu)
      : document.createDocumentFragment();

    return html`${grid}${targetZone}${overlay}`;
  }
}
